import { InlineNotification } from '@hologram-dimension/inline-notification';
import { Link } from '@hologram-dimension/link';
import { PanelNotification } from '@hologram-dimension/panel-notification';
import { BannerNotification } from '@hologram-dimension/banner-notification';
import { Popover } from '@hologram-dimension/popover';
import {
  useSimDetails,
  useSimDeviceDetails,
  useSimMostRecentSession,
  useSimProfiles,
  useSimStatuses,
} from 'api';
import useDevice from 'api/legacy/devices/useDevice';
import ReleaseFlag from 'api/legacy/releaseFlags/enumReleaseFlags';
import useOpenSessions from 'api/legacy/session/useOpenSessions';
import clsx from 'clsx/lite';
import useUserInfo from 'hooks/User/useUserInfo';
import { DateTime } from 'luxon';
import {
  Suspense,
  lazy,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from 'react';
import type { SimsDaoMostRecentSession } from 'types/__generated__/SimInventoryApi';
import getUserFriendlyBytes from 'utils/bytes/getUserFriendlyBytes';
import apiDateToDateTime from 'utils/dates/apiDateToDateTime';
import formatAndNormalizeDuration from 'utils/dates/formatAndNormalizeDuration';
import formatDateTime from 'utils/dates/formatDateTime';
import getNormalizedRelativeDuration from 'utils/dates/getNormalizedRelativeDuration';
import useStatusMap from 'utils/hooks/useStatusMap';
import stringToFloat from 'utils/numbers/stringToFloat';
import ApnInfoIcon from 'components/ApnInfoIcon/ApnInfoIcon';
import PublicIpAddressField from 'components/PublicIpAddressField/PublicIpAddressField';
import { useUrl } from 'utils/hooks';
import Field from '../Field/Field';
import FieldWrapper from '../FieldWrapper/FieldWrapper';
import IconWrapper from './IconWrapper/IconWrapper';
import styles from './SimStatusPanel.module.css';

const Mapbox = lazy(() => import('components/Mapbox/Mapbox'));

interface SimMostRecentSessionInfoProps {
  hologramSimId?: HologramSimId;
  usageExitedTestingUrl: string;
}

const SimMostRecentSessionInfo: FC<SimMostRecentSessionInfoProps> = ({
  hologramSimId,
  usageExitedTestingUrl,
}) => {
  const {
    mostRecentSession,
    error: mostRecentSessionError,
    isLoading: mostRecentSessionIsLoading,
  } = useSimMostRecentSession(hologramSimId);
  const {
    simDetails,
    error: simDetailsError,
    isLoading: simDetailsIsLoading,
  } = useSimDetails(hologramSimId);
  const {
    simProfiles,
    error: simProfilesError,
    isLoading: simProfilesIsLoading,
  } = useSimProfiles(hologramSimId);

  // TODO: See this ticket for a starting point on getting rid of all these refs: https://app.asana.com/0/1203386193166138/1205953988652319/f
  const lineStart = useRef<HTMLDivElement>(null);
  const lineEnd = useRef<HTMLDivElement>(null);
  const line = useRef<SVGSVGElement>(null);
  const [lineHeight, setLineHeight] = useState<number>(0);

  useEffect(() => {
    const handleLineResizing = () => {
      if (line.current && lineStart.current && lineEnd.current) {
        line.current.style.top = `${
          lineStart.current.offsetTop + lineStart.current.offsetHeight / 2
        }px`;
        line.current.style.left = `${
          lineStart.current.offsetLeft + lineStart.current.clientWidth / 2 - 1
        }px`;
        setLineHeight(lineEnd.current.offsetTop - lineStart.current.offsetTop);
      }
    };
    window.addEventListener('resize', handleLineResizing);
    handleLineResizing();
    return () => window.removeEventListener('resize', handleLineResizing);
  });

  // TODO: Remove when we have real connected status

  const {
    deviceDetails,
    isLoading: isDeviceDetailsLoading,
    error: deviceDetailsError,
  } = useSimDeviceDetails(hologramSimId);

  const {
    device,
    error: deviceError,
    isLoading: deviceLoading,
  } = useDevice(
    simDetails.organizationId && deviceDetails.claimedDeviceId
      ? deviceDetails.claimedDeviceId
      : undefined,
    simDetails.organizationId
  );
  const { isSuperAdmin } = useUserInfo();

  const {
    error: openSessionsError,
    isLoading: openSessionsLoading,
    deviceIdsWithOpenSessions,
  } = useOpenSessions(simDetails.organizationId, isSuperAdmin || !!simDetails.organizationId);

  const { networkStatusGroups } = useSimStatuses();

  const realNetworkState = useMemo(() => {
    if (
      deviceDetails.claimedDeviceId &&
      deviceIdsWithOpenSessions.has(deviceDetails.claimedDeviceId) &&
      !networkStatusGroups?.deactivated?.includes(simDetails.networkStatus as any)
    ) {
      if (networkStatusGroups?.predeployment?.includes(simDetails.networkStatus as any)) {
        return 'TEST-CONNECTED';
      }
      return 'CONNECTED';
    }
    return simDetails.networkStatus;
  }, [
    deviceDetails.claimedDeviceId,
    deviceIdsWithOpenSessions,
    simDetails.networkStatus,
    networkStatusGroups?.predeployment,
    networkStatusGroups?.deactivated,
  ]);

  const realMostRecentSession = useMemo<SimsDaoMostRecentSession>(() => {
    if (
      realNetworkState?.includes('CONNECTED') &&
      device.lastsession &&
      device.lastsession.active
    ) {
      const lastSession = device.lastsession;

      const deviceMostRecentSession: SimsDaoMostRecentSession = {
        bytes: lastSession.bytes,
        cellTowerId: lastSession.cellid ? parseInt(lastSession.cellid, 10) : -1,
        isReportingWindowWithUsage: lastSession.is_reporting_window_with_usage
          ? parseInt(lastSession.is_reporting_window_with_usage, 10)
          : 0,
        latitude: `${lastSession.latitude}`,
        longitude: `${lastSession.longitude}`,
        network: lastSession.network_name,
        country: '',
        hologramSimId,
        sessionId: 12345, // Fake number to make it appear
        rat: lastSession.radio_access_technology,
        // E.g. "2024-12-01 09:45:44". Whereas most recent session expects 2024-10-17T20:57:29Z
        startTime: lastSession.session_begin
          ? (DateTime.fromFormat(lastSession.session_begin, 'yyyy-MM-dd HH:mm:ss', {
              zone: 'utc',
            }).toISO() ?? undefined)
          : undefined,
        lac: lastSession.lac,
        simProfileId: lastSession.linkid ? parseInt(lastSession.linkid, 10) : -1,
        tadig: lastSession.tadig,
      };

      return deviceMostRecentSession;
    }

    return mostRecentSession;
  }, [device.lastsession, hologramSimId, mostRecentSession, realNetworkState]);

  const { statusInfo } = useStatusMap(realNetworkState);

  const { releaseFlags } = useUserInfo();

  const showTowerInfo = !releaseFlags.has(ReleaseFlag.HIDE_TOWER_LOCATIONS);
  const hasPredeployment = releaseFlags.has(ReleaseFlag.FEATURE_PREDEPLOYMENT);

  let content: ReactNode;

  const towerIsUnknown = realMostRecentSession.cellTowerId === -1;

  const error =
    mostRecentSessionError ||
    simDetailsError ||
    simProfilesError ||
    deviceDetailsError ||
    deviceError ||
    openSessionsError;
  const isLoading =
    mostRecentSessionIsLoading ||
    simDetailsIsLoading ||
    simProfilesIsLoading ||
    isDeviceDetailsLoading ||
    deviceLoading ||
    openSessionsLoading;

  if (error) {
    content = (
      <FieldWrapper
        label=""
        value={
          <InlineNotification variant="system-error">
            Failed to load most recent session for this SIM.
          </InlineNotification>
        }
        indented
      />
    );
  } else {
    let lastConnection: ReactNode;
    let lastSession: ReactNode;
    let network: ReactNode;
    let localTower: ReactNode;
    let loadingText: ReactNode;
    let country: ReactNode;

    if (isLoading) {
      loadingText = <div className={styles.recentLoadingText}>Finding last session...</div>;
    } else {
      lastConnection = realMostRecentSession?.sessionId && (
        <Field
          label={realNetworkState?.includes('CONNECTED') ? 'Session opened' : 'Last seen'}
          value={
            <strong>
              {getNormalizedRelativeDuration({
                start: apiDateToDateTime(
                  realNetworkState?.includes('CONNECTED')
                    ? realMostRecentSession.startTime
                    : realMostRecentSession.endTime
                ),
                top: 2,
              })}
            </strong>
          }
        />
      );

      let endField;
      if (realNetworkState?.includes('CONNECTED')) {
        if (realMostRecentSession?.isReportingWindowWithUsage) {
          endField = 'Reporting window open';
        } else {
          endField = 'Connected';
        }
      } else {
        endField = (
          <Popover
            id="session-end-time"
            className={styles.numeric}
            content={formatDateTime(realMostRecentSession.endTime, '12hrlocal')}
          >
            <span className={styles.numeric}>
              {formatDateTime(realMostRecentSession.endTime, '24hrutc')}
            </span>
          </Popover>
        );
      }
      lastSession = realMostRecentSession?.sessionId && (
        <>
          <Field
            label="Data usage"
            valueClassname={styles.numeric}
            value={getUserFriendlyBytes(realMostRecentSession.bytes, 'long', 'decimal')}
          />
          {!!realMostRecentSession?.isReportingWindowWithUsage && (
            <Field label="Type" value="Reporting window" />
          )}
          <Field
            label="Session start"
            value={
              <Popover
                id="session-start-time"
                className={styles.numeric}
                content={formatDateTime(realMostRecentSession.startTime, '12hrlocal')}
              >
                <span className={styles.numeric}>
                  {formatDateTime(realMostRecentSession.startTime, '24hrutc')}
                </span>
              </Popover>
            }
          />
          <Field
            label="Session end"
            valueClassname={clsx(
              realNetworkState?.includes('CONNECTED') ? styles.endOpen : styles.numeric
            )}
            value={endField}
          />
          <Field
            label="Session duration"
            valueClassname={styles.numeric}
            value={formatAndNormalizeDuration({
              start: apiDateToDateTime(realMostRecentSession.startTime),
              end: apiDateToDateTime(realMostRecentSession.endTime),
              top: 2,
            })}
          />
        </>
      );

      network = realMostRecentSession?.sessionId && (
        <>
          <Field label="Network" value={realMostRecentSession.network} />
          <Field label="RAT" value={realMostRecentSession.rat} />
        </>
      );

      if (realMostRecentSession.sessionId) {
        if (realMostRecentSession.country) {
          country = (
            <>
              {realMostRecentSession.country}
              {towerIsUnknown && (
                <>
                  {' '}
                  <span className={styles.unknownCellTower}>
                    (location not available for this session)
                  </span>
                </>
              )}
            </>
          );
        } else if (realNetworkState?.includes('CONNECTED')) {
          country = <span className={styles.unknownCellTower}>Available after session close</span>;
        } else {
          country = <span className={styles.unknownCellTower}>Location not available</span>;
        }
      }

      localTower = realMostRecentSession?.sessionId && (
        <>
          <Field label="Country" value={country} />
          {showTowerInfo &&
            !towerIsUnknown &&
            realMostRecentSession.latitude &&
            realMostRecentSession.longitude && (
              <Field
                label="Tower location"
                valueClassname={styles.numeric}
                value={
                  <Link
                    href={`https://www.google.com/maps/search/?api=1&query=${realMostRecentSession.latitude}%2C${realMostRecentSession.longitude}`}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    type="inline"
                  >
                    {`${realMostRecentSession.latitude} ${realMostRecentSession.longitude}`}
                  </Link>
                }
              />
            )}
          {showTowerInfo && !towerIsUnknown && (
            <Field
              label="Cell tower ID"
              valueClassname={clsx(
                realMostRecentSession.cellTowerId === 0 && styles.unknownCellTower,
                styles.numeric
              )}
              value={
                realMostRecentSession.cellTowerId === 0
                  ? 'Unknown'
                  : realMostRecentSession.cellTowerId?.toString()
              }
            />
          )}
        </>
      );
    }

    content = (
      <>
        {loadingText}
        <FieldWrapper
          label={
            <>
              <IconWrapper
                elementRef={lineStart}
                iconName={realNetworkState?.includes('CONNECTED') ? 'StatusConnected' : 'Cloud'}
                colorVariant={
                  realNetworkState?.includes('CONNECTED') ? statusInfo?.colorVariant : undefined
                }
                diminished={!lastConnection}
              />
              {realNetworkState?.includes('CONNECTED') ? 'Connected' : 'Last connection'}
            </>
          }
          labelClassname={!lastConnection ? styles.diminished : undefined}
          value={
            lastConnection || <div className={styles.noSession}>No session data shared yet.</div>
          }
        />
        <FieldWrapper
          label={
            <>
              <IconWrapper
                iconName="CloudDataTransfer"
                colorVariant={
                  realNetworkState?.includes('CONNECTED') ? statusInfo?.colorVariant : undefined
                }
                diminished={!lastSession}
              />
              {realNetworkState?.includes('CONNECTED') ? 'Open session' : 'Last session'}
            </>
          }
          labelClassname={!lastSession ? styles.diminished : undefined}
          value={lastSession}
        />
        <FieldWrapper
          label={
            <>
              <IconWrapper
                iconName="CellTower"
                colorVariant={
                  realNetworkState?.includes('CONNECTED') ? statusInfo?.colorVariant : undefined
                }
                diminished={!localTower}
              />
              Local tower
            </>
          }
          labelClassname={!localTower ? styles.diminished : undefined}
          value={localTower}
        />
        <FieldWrapper
          label={
            <>
              <IconWrapper
                iconName="Connectivity"
                colorVariant={
                  realNetworkState?.includes('CONNECTED') ? statusInfo?.colorVariant : undefined
                }
                diminished={!network}
              />
              Network
            </>
          }
          labelClassname={!network ? styles.diminished : undefined}
          value={network}
        />
        <FieldWrapper
          label={
            <>
              <IconWrapper
                elementRef={lineEnd}
                iconName="Compass"
                colorVariant={
                  realNetworkState?.includes('CONNECTED') ? statusInfo?.colorVariant : undefined
                }
              />
              Mobile core operator
            </>
          }
          value={
            <>
              <Field label="Required device APN" value={<ApnInfoIcon profiles={simProfiles} />} />
              {lastConnection && (
                <Field
                  label="Profile name"
                  value={
                    simProfiles.find((p) => p.simProfileId === realMostRecentSession.simProfileId)
                      ?.profileName
                  }
                />
              )}
              <PublicIpAddressField hologramSimId={hologramSimId} />
            </>
          }
        />
      </>
    );
  }

  const longitude = stringToFloat(realMostRecentSession?.longitude);
  const latitude = stringToFloat(realMostRecentSession?.latitude);

  // Parse and validate dates for comparison
  const activationDateTime = apiDateToDateTime(simDetails.activationTime);
  const sessionStartDateTime = apiDateToDateTime(realMostRecentSession.startTime);

  // Check if session started before or at activation time
  const isPredeploymentSession =
    activationDateTime && sessionStartDateTime && sessionStartDateTime <= activationDateTime;

  const url = useUrl({
    url: usageExitedTestingUrl,
    replacers: [
      {
        placeholder: 'orgid',
        value: simDetails.organizationId?.toString() ?? '',
      },
    ],
  });
  return (
    <>
      {hasPredeployment && isPredeploymentSession && url && (
        <BannerNotification variant="note" className={styles.predeploymentBanner}>
          <span>
            This session opened during pre-deployment testing before the SIM was activated. View the{' '}
            <Link to={url} type="inline">
              Activation history report
            </Link>{' '}
            for more details.
          </span>
        </BannerNotification>
      )}
      <div className={clsx(styles.panelSectionContent, styles.panelSectionContentWithLine)}>
        <svg
          ref={line}
          className={clsx(
            styles.line,
            realNetworkState?.includes('CONNECTED')
              ? styles[statusInfo?.colorVariant as keyof typeof styles]
              : styles.default
          )}
          xmlns="http://www.w3.org/2000/svg"
          width="2"
          height={lineHeight}
          viewBox={`0 0 2 ${lineHeight}`}
          fill="none"
          strokeDasharray="3 3"
          strokeWidth="1.5"
        >
          <path d={`M1,0 L 1,${lineHeight}`} />
        </svg>
        {content}
      </div>
      <div className={styles.panelSectionAction}>
        {showTowerInfo &&
          !isLoading &&
          !error &&
          realMostRecentSession?.sessionId &&
          (!towerIsUnknown && latitude && longitude ? (
            <Suspense
              fallback={
                <PanelNotification className={styles.mapPlaceholder}>Loading...</PanelNotification>
              }
            >
              <Mapbox zoom={10} height={240} width={240} lat={latitude} long={longitude} />
            </Suspense>
          ) : (
            <PanelNotification className={styles.mapPlaceholder}>
              {simDetails.profileName?.includes('USA-2')
                ? 'Location data is not available for this network.'
                : 'Location data is not available for this session.'}
            </PanelNotification>
          ))}
      </div>
    </>
  );
};

export default SimMostRecentSessionInfo;
